"use client";
import React, { forwardRef, useState } from "react";
import { cn } from "@/lib/utils";
import { Eye, EyeOff } from "lucide-react";
import { BackendLabel } from "./backend-label";
import { backendInputClass } from "./backend-ui";
import ValidationPopover, {
  type ValidationErrorT,
  type ValidationPopoverBgT,
} from "../ui/validation-popover";
import {
  useController,
  type Control,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";

export interface BackendInputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  // Additional props
  error?:
    | boolean
    | string
    | string[]
    | ValidationErrorT[]
    | (string | ValidationErrorT)[];
  containerClassName?: string;
  bgVariant?: ValidationPopoverBgT;
}

export interface BackendInputWithLabelProps extends BackendInputProps {
  label?: string;
  labelClassName?: string;
  showPasswordToggle?: boolean;
  required?: boolean;
  helperText?: string; // helperText belongs here, not in low-level BackendInputProps
}

// New props for react-hook-form integration
export interface BackendControlledInputProps<
  TFieldValues extends FieldValues = FieldValues,
> extends Omit<
    BackendInputWithLabelProps,
    "name" | "value" | "onChange" | "error"
  > {
  name: FieldPath<TFieldValues>;
  control: Control<TFieldValues>;
}

const BackendInput = forwardRef<HTMLInputElement, BackendInputProps>(
  (
    {
      className,
      type = "text",
      error = false,
      containerClassName,
      bgVariant = "accent",
      ...props
    },
    ref
  ) => {
    const [popoverOpen, setPopoverOpen] = useState(false);

    const inputStyles = cn(
      backendInputClass,
      error && "border-destructive focus:border-destructive",
      type === "number" &&
        "[&::-webkit-inner-spin-button]:hidden [&::-webkit-outer-spin-button]:hidden",
      className
    );

    // Determine if there's an error (either boolean true or non-empty string)
    const hasError = Boolean(error);

    // Error handling for validation popover
    let errorMessages: (string | ValidationErrorT)[] = [];
    if (typeof error === "string" && error) {
      errorMessages = [error];
    } else if (Array.isArray(error)) {
      errorMessages = error.filter((msg) => {
        if (typeof msg === "string") return msg;
        if (typeof msg === "object" && "message" in msg) {
          return (msg as ValidationErrorT).message;
        }
        return false;
      });
    }

    const shouldShowValidation = hasError && errorMessages.length > 0;

    // Close popover automatically when validation errors disappear
    React.useEffect(() => {
      if (!shouldShowValidation && popoverOpen) {
        setPopoverOpen(false);
      }
    }, [shouldShowValidation, popoverOpen]);

    // Handle input click to open popover (only if validation enabled and not already open)
    const handleInputClick = (e: React.MouseEvent<HTMLInputElement>) => {
      if (shouldShowValidation && !popoverOpen) {
        setPopoverOpen(true);
      }
      props.onClick?.(e);
    };

    // Handle input focus - don't open popover automatically
    const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      props.onFocus?.(e);
    };

    // Handle touch events for mobile devices (only if validation enabled and not already open)
    const handleTouchStart = (e: React.TouchEvent<HTMLInputElement>) => {
      if (shouldShowValidation && !popoverOpen) {
        setPopoverOpen(true);
      }
      props.onTouchStart?.(e);
    };

    return (
      <div className="flex w-full flex-col">
        <div className={cn("relative text-sm", containerClassName)}>
          {/* Input with Popover - only show if validation is enabled */}
          <ValidationPopover
            error={
              shouldShowValidation
                ? (errorMessages as (string | ValidationErrorT)[])
                : undefined
            }
            isOpen={shouldShowValidation ? popoverOpen : false}
            onOpenChange={shouldShowValidation ? setPopoverOpen : undefined}
            triggerRef={ref}
            bgVariant={bgVariant}
          >
            <input
              data-slot="backend-input"
              type={type}
              className={inputStyles}
              ref={ref}
              aria-invalid={hasError || undefined}
              {...props}
              onClick={handleInputClick}
              onFocus={handleInputFocus}
              onTouchStart={handleTouchStart}
            />
          </ValidationPopover>

          {/* Fehlermeldung entfernt - Validation Popover wird verwendet */}
        </div>
      </div>
    );
  }
);

const BackendInputWithLabel = forwardRef<
  HTMLInputElement,
  BackendInputWithLabelProps & { unit?: string }
>(
  (
    {
      className,
      label,
      labelClassName,
      id,
      type,
      showPasswordToggle = false,
      required,
      bgVariant = "accent",
      error,
      unit,
      helperText, // Extract helperText to prevent it from being passed to BackendInput
      ...props
    },
    ref
  ) => {
    const [isPasswordVisible, setIsPasswordVisible] = useState<boolean>(false);

    const togglePasswordVisibility = () => {
      setIsPasswordVisible((prev) => !prev);
    };

    const isPassword = type === "password";
    const showToggle = isPassword && showPasswordToggle;
    const currentType = isPassword && isPasswordVisible ? "text" : type;

    return (
      <div className="flex flex-col gap-1 sm:gap-1.5">
        {label && (
          <BackendLabel
            htmlFor={id}
            required={required}
            className={labelClassName}
          >
            {label}
          </BackendLabel>
        )}
        <div className="relative flex items-center">
          <BackendInput
            id={id}
            className={cn(showToggle && "pr-10", className)}
            type={currentType}
            ref={ref}
            error={error}
            bgVariant={bgVariant}
            {...props}
          />
          {/* Unit Anzeige rechts im Input */}
          {unit && (
            <span className="pointer-events-none absolute right-3 text-sm text-[var(--c-muted)]">
              {unit}
            </span>
          )}

          {showToggle && (
            <button
              type="button"
              onClick={togglePasswordVisibility}
              className="absolute top-1/2 right-3 -translate-y-1/2 cursor-pointer text-[var(--c-muted)] transition-colors hover:text-[var(--c-ink)] sm:right-4"
              aria-label={
                isPasswordVisible ? "Passwort verbergen" : "Passwort anzeigen"
              }
            >
              {isPasswordVisible ? <Eye size={20} /> : <EyeOff size={20} />}
            </button>
          )}
        </div>
        {/* Helper Text - shown only when provided and no error */}
        {helperText && !error && (
          <p className="mt-0 text-xs text-[var(--c-muted)]">{helperText}</p>
        )}
      </div>
    );
  }
);

BackendInputWithLabel.displayName = "BackendInputWithLabel";

// New controlled input component for react-hook-form
const BackendControlledInput = <
  TFieldValues extends FieldValues = FieldValues,
>({
  name,
  control,
  ...props
}: BackendControlledInputProps<TFieldValues>) => {
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
  });

  return <BackendInputWithLabel {...props} {...field} error={error?.message} />;
};

BackendInput.displayName = "BackendInput";
BackendInputWithLabel.displayName = "BackendInputWithLabel";
BackendControlledInput.displayName = "BackendControlledInput";

export { BackendInput, BackendInputWithLabel, BackendControlledInput };
